"use client"

import { useCallback, useMemo } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { X } from "lucide-react"
import { useCatalogTransition } from "@/components/public/CatalogTransitionContext"
import { useTranslations } from "@/lib/i18n/client"
import {
  cn,
  FILTER_PENDING_CLASSES,
  getFabricanteLabel,
} from "@/lib/utils"
import { resolveI18n } from "@/lib/i18n/resolve"
import type { I18nText } from "@/lib/types"
import {
  YEAR_MIN,
  YEAR_MAX,
} from "@/components/public/CatalogFiltersPanel"

interface CategoriaOption {
  slug: string
  nome: I18nText
}

interface FabricanteOption {
  slug: string
  nome: string
}

interface ModeloOption {
  slug: string
  nome: I18nText
  fabricante_slug: string
  fabricante_nome: string
  categoria_slug: string
}

interface CatalogChipsProps {
  categorias: CategoriaOption[]
  fabricantes: FabricanteOption[]
  modelos: ModeloOption[]
}

function parseList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

export function CatalogChips({ categorias, fabricantes, modelos }: CatalogChipsProps) {
  const { locale, t } = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { isPending, startTransition } = useCatalogTransition()

  const categoriaSel = useMemo(
    () => parseList(searchParams.get("categoria") ?? ""),
    [searchParams],
  )
  const fabricanteSel = useMemo(
    () => parseList(searchParams.get("fabricante") ?? ""),
    [searchParams],
  )
  const modeloSel = useMemo(
    () => parseList(searchParams.get("modelo") ?? ""),
    [searchParams],
  )
  const localizacaoSel = useMemo(
    () => parseList(searchParams.get("localizacao") ?? ""),
    [searchParams],
  )
  const q = searchParams.get("q") ?? ""
  const anoMin = Number(searchParams.get("ano_min")) || YEAR_MIN
  const anoMax = Number(searchParams.get("ano_max")) || YEAR_MAX
  const destaque = searchParams.get("destaque") === "1"
  const comFotos = searchParams.get("com_fotos") === "1"

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const params = new URLSearchParams(searchParams.toString())
      for (const [key, value] of Object.entries(updates)) {
        if (value) params.set(key, value)
        else params.delete(key)
      }
      params.delete("pagina")
      const qs = params.toString()
      startTransition(() => {
        router.push(qs ? `/aeronaves?${qs}` : "/aeronaves")
      })
    },
    [router, searchParams, startTransition],
  )

  const clearAll = useCallback(() => {
    startTransition(() => {
      router.push("/aeronaves")
    })
  }, [router, startTransition])

  const chips: { key: string; label: string; onRemove: () => void }[] = []

  if (q) {
    chips.push({
      key: "q",
      label: `"${q}"`,
      onRemove: () => updateParams({ q: "" }),
    })
  }

  categoriaSel.forEach((slug) => {
    const cat = categorias.find((c) => c.slug === slug)
    chips.push({
      key: `cat-${slug}`,
      label: cat ? resolveI18n(cat.nome, locale) : slug,
      onRemove: () => {
        const next = categoriaSel.filter((s) => s !== slug)
        updateParams({ categoria: next.join(",") })
      },
    })
  })

  fabricanteSel.forEach((slug) => {
    const fab = fabricantes.find((f) => f.slug === slug)
    chips.push({
      key: `fab-${slug}`,
      label: fab ? getFabricanteLabel(fab, t) : slug,
      onRemove: () => {
        const next = fabricanteSel.filter((s) => s !== slug)
        updateParams({ fabricante: next.join(",") })
      },
    })
  })

  modeloSel.forEach((slug) => {
    const m = modelos.find((x) => x.slug === slug)
    const label = m
      ? `${getFabricanteLabel({ slug: m.fabricante_slug, nome: m.fabricante_nome }, t)} ${resolveI18n(m.nome, locale)}`
      : slug
    chips.push({
      key: `mod-${slug}`,
      label,
      onRemove: () => {
        const next = modeloSel.filter((s) => s !== slug)
        updateParams({ modelo: next.join(",") })
      },
    })
  })

  localizacaoSel.forEach((loc) => {
    chips.push({
      key: `loc-${loc}`,
      label: loc,
      onRemove: () => {
        const next = localizacaoSel.filter((s) => s !== loc)
        updateParams({ localizacao: next.join(",") })
      },
    })
  })

  if (anoMin > YEAR_MIN || anoMax < YEAR_MAX) {
    chips.push({
      key: "ano",
      label: `${anoMin} – ${anoMax}`,
      onRemove: () => updateParams({ ano_min: "", ano_max: "" }),
    })
  }

  if (destaque) {
    chips.push({
      key: "destaque",
      label: t.catalog.featured,
      onRemove: () => updateParams({ destaque: "" }),
    })
  }

  if (comFotos) {
    chips.push({
      key: "com_fotos",
      label: t.catalog.withPhotos,
      onRemove: () => updateParams({ com_fotos: "" }),
    })
  }

  if (chips.length === 0) return null

  return (
    <div
      aria-busy={isPending}
      className={cn(
        "mb-4 flex flex-wrap items-center gap-2",
        isPending && FILTER_PENDING_CLASSES,
      )}
    >
      <span className="mr-1 text-[11px] uppercase tracking-[0.08em] text-slate-500">
        {t.catalog.active}:
      </span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border-[0.5px] border-navy-200 bg-navy-50 px-3 py-1 text-xs font-medium text-navy-800"
        >
          <span className="max-w-[180px] truncate" title={chip.label}>
            {chip.label}
          </span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={t.catalog.removeFilter}
            className="-mr-1.5 inline-flex shrink-0 items-center justify-center p-1.5 text-navy-800 hover:text-navy-900"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      {chips.length >= 2 && (
        <button
          type="button"
          onClick={clearAll}
          className="text-xs text-slate-500 underline underline-offset-2 transition-colors hover:text-navy-500"
        >
          {t.catalog.clearAll}
        </button>
      )}
    </div>
  )
}
